import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, PageHeader, Pill } from "@/components/common";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  DollarSign, TrendingUp, CheckSquare, Package, Megaphone, Palette, FileText, ArrowUpRight, ArrowDownToLine,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Metric({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${tone === "emerald" ? "text-emerald-400" : "text-slate-500"}`} />}
      </div>
      <div className="mt-2 text-2xl font-head font-bold text-slate-100">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}

export default function Overview() {
  const [sum, setSum] = useState(null);

  useEffect(() => { api.get("/analytics/summary").then(({ data }) => setSum(data)).catch(() => {}); }, []);

  const chartData = (sum?.series || []).map((m) => ({
    date: m.date.slice(5),
    revenue: m.revenue,
    profit: Math.round(m.revenue - m.pod_cost - m.ad_spend - m.revenue * 0.03),
  }));

  const quick = [
    { to: "/research", label: "Run Research", icon: TrendingUp },
    { to: "/designs", label: "Generate Design", icon: Palette },
    { to: "/listings", label: "Write Listing", icon: FileText },
    { to: "/queue", label: "Review Queue", icon: CheckSquare },
  ];

  return (
    <>
      <PageHeader
        title="Overview & Control"
        subtitle="Live command center for your autonomous print-on-demand operation."
        testid="overview-header"
      >
        <Pill tone="emerald">Live</Pill>
      </PageHeader>

      <Card className="p-6 border-emerald-500/20 relative overflow-hidden" data-testid="profit-widget">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="text-xs text-slate-400 font-mono uppercase tracking-wider">Real Profit (Net, 30d)</div>
            <div className="mt-2 text-4xl font-head font-extrabold text-emerald-400" data-testid="profit-widget-amount">
              {money(sum?.profit)}
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 text-emerald-400"><ArrowUpRight className="w-4 h-4" />+24.8%</span>
              <span className="text-slate-500">vs prior 30d</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Pill tone="blue">{sum ? `${sum.roas}x ROAS` : "—"}</Pill>
              <Pill tone="emerald">{sum ? `${sum.margin_pct}% margin` : "—"}</Pill>
            </div>
            <Link to="/wallet" data-testid="overview-withdraw-button" className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors">
              <ArrowDownToLine className="w-4 h-4" /> Withdraw to bank
            </Link>
          </div>
          <div className="lg:col-span-2 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#12161F", border: "1px solid #1e293b", borderRadius: 8, color: "#e2e8f0" }} />
                <Area type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric testid="metric-revenue" label="Gross Revenue" value={money(sum?.revenue)} sub="last 30 days" icon={DollarSign} />
        <Metric testid="metric-costs" label="POD + Ad Cost" value={money((sum?.pod_cost || 0) + (sum?.ad_spend || 0))} sub={`${money(sum?.ad_spend)} ad spend`} icon={Megaphone} />
        <Metric testid="metric-published" label="Live Products" value={sum?.published_products ?? "—"} sub="published to stores" icon={Package} tone="emerald" />
        <Metric testid="metric-pending" label="Pending Review" value={sum?.pending_reviews ?? "—"} sub="awaiting your approval" icon={CheckSquare} />
      </div>

      <div>
        <h3 className="font-head text-lg font-medium text-slate-200 mb-3">Quick actions</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {quick.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, "-")}`}>
              <Card className="p-5 hover:border-emerald-500/40 transition-colors cursor-pointer group">
                <q.icon className="w-6 h-6 text-emerald-400 mb-3" />
                <div className="font-medium text-slate-200 group-hover:text-emerald-300 transition-colors">{q.label}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
